// src/pages/dashboard/Dashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../layout/Navbar';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LOGIN_ORG_LIST_ENDPOINT } from '../../config/api';
import { decryptData, encryptData } from '../../utils/encrypt';
import OrgCard from './cards/OrgCard';
import SOCard from './cards/SOCard';
import SOPlanningCard from './cards/SOPlanningCard';

function Dashboard({ onLogout }) {
  const [user, setUser] = useState({ user_name: 'Guest', user_id: null, user_token: '' });

  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgError, setOrgError] = useState('');

  // Restore selected org from localStorage (react-select option object or null)
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const saved = localStorage.getItem('suyog_selected_org');
    return saved ? JSON.parse(saved) : null;
  });

  const [showNavbar, setShowNavbar] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  // Helpers
  const persistSelectedOrgInsideEncryptedUser = useCallback((selected) => {
    const encrypted = localStorage.getItem('suyog_user');
    if (!encrypted) return;
    try {
      const parsed = decryptData(encrypted);
      const updated = { ...parsed, selected_org: selected || null };
      const reEncrypted = encryptData(updated);
      localStorage.setItem('suyog_user', reEncrypted);
    } catch {
      console.error('Failed to update organization in local storage.');
    }
  }, []);

  const setSelectedOrgEverywhere = useCallback((optionOrNull) => {
    setSelectedOrg(optionOrNull);
    persistSelectedOrgInsideEncryptedUser(optionOrNull);
    if (optionOrNull) {
      localStorage.setItem('suyog_selected_org', JSON.stringify(optionOrNull));
    } else {
      localStorage.removeItem('suyog_selected_org');
    }
  }, [persistSelectedOrgInsideEncryptedUser]);

  const loadOrganizations = useCallback(async (u, { autoSelectIfSingle = true } = {}) => {
    if (!u?.user_id || !u?.user_token) return [];
    setLoadingOrgs(true);
    setOrgError('');
    try {
      const res = await fetch(LOGIN_ORG_LIST_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: u.user_id, user_token: u.user_token })
      });
      const data = await res.json();

      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data?.assigned)) {
        list = data.assigned;
      }

      // Normalize to react-select options
      const normalized = list
        .filter(item => item && (item.id || item.org_id))
        .map(item => ({
          value: item.id ?? item.org_id,
          label: item.org_name ?? item.name ?? `Org #${item.id ?? item.org_id}`
        }));

      setOrgs(normalized);

      // If no selectedOrg currently and exactly one org — auto-select
      if (autoSelectIfSingle && !selectedOrg && normalized.length === 1) {
        setSelectedOrgEverywhere(normalized[0]);
      }

      return normalized;
    } catch (e) {
      console.error('Error fetching organizations:', e);
      setOrgs([]);
      setOrgError('Failed to load organizations. Please try Reload.');
      return [];
    } finally {
      setLoadingOrgs(false);
    }
  }, [LOGIN_ORG_LIST_ENDPOINT, selectedOrg, setSelectedOrgEverywhere]);

  // Initial load: user + orgs
  useEffect(() => {
    const encrypted = localStorage.getItem('suyog_user');
    if (!encrypted) return;

    try {
      const parsed = decryptData(encrypted);
      const u = {
        user_name: parsed.user_name || 'User',
        user_id: parsed.user_id || null,
        user_token: parsed.user_token || ''
      };
      setUser(u);
      loadOrganizations(u, { autoSelectIfSingle: true });
    } catch {
      setUser({ user_name: 'Unknown', user_id: null, user_token: '' });
    }
  }, [loadOrganizations]);

  const handleOrgChange = (selected) => {
    setSelectedOrgEverywhere(selected || null);
    setShowNavbar(false);
  };

  const handleProcess = () => {
    setShowNavbar(true);
  };

  const orgOptions = orgs;

  // If previously saved selectedOrg is not present in fresh list, clear it
  useEffect(() => {
    if (selectedOrg && !orgs.find(o => o.value === selectedOrg.value)) {
      setSelectedOrgEverywhere(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs]);

  return (
    <>
      {showNavbar && <Navbar />}

      <div className="container mt-5">
        <OrgCard
          userName={user.user_name}
          orgOptions={orgOptions}
          selectedOrg={selectedOrg}
          onOrgChange={handleOrgChange}
          onProcess={handleProcess}
          onLogout={() => {
            localStorage.removeItem('suyog_user');
            localStorage.removeItem('suyog_selected_org');
            onLogout();
          }}
          // These extra props are harmless if OrgCard ignores them
          reloading={loadingOrgs}
          reloadHint={orgError}
        />

        {/* 🔄 Force Reload row (shows if no org selected OR org list empty) */}
        <div className="d-flex align-items-center gap-2 mt-3">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={loadingOrgs || !user.user_id}
            onClick={() => loadOrganizations(user, { autoSelectIfSingle: true })}
            title="Force reload organization list from server"
          >
            {loadingOrgs ? 'Reloading…' : '🔄 Reload Organizations'}
          </button>

          <small className="text-muted">
            {orgError
              ? orgError
              : orgs.length === 0
                ? 'No organizations loaded. Click Reload to fetch.'
                : selectedOrg
                  ? `Selected: ${selectedOrg.label}`
                  : 'Select an organization to proceed.'}
          </small>
        </div>

        {selectedOrg && user.user_id && (
          <>
            {/* 📅 Date Range Picker */}
            <div className="d-flex gap-2 align-items-center mt-4 mb-3">
              <label className="form-label mb-0">📅 Date Range:</label>
              <DatePicker
                selected={dateRange.start}
                onChange={(date) => setDateRange({ ...dateRange, start: date })}
                selectsStart
                startDate={dateRange.start}
                endDate={dateRange.end}
                placeholderText="From"
                className="form-control"
              />
              <DatePicker
                selected={dateRange.end}
                onChange={(date) => setDateRange({ ...dateRange, end: date })}
                selectsEnd
                startDate={dateRange.start}
                endDate={dateRange.end}
                placeholderText="To"
                className="form-control"
              />
            </div>

            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <SOCard
                  userId={user.user_id}
                  orgId={selectedOrg.value}
                  dateRange={dateRange}
                />
              </div>
              <div className="col-md-6 col-lg-4">
                <SOPlanningCard
                  userId={user.user_id}
                  orgId={selectedOrg.value}
                  dateRange={dateRange}
                />
              </div>
              <div className="col-md-6 col-lg-4">{/* Future cards */}</div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Dashboard;
